import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { useAddToCart } from '@/queries/useCart'
import { MenuItem } from '@/queries/useMenuItems'
import { useCartStore } from '@/store/cartStore'
import { getLocalizedText } from '@/utils/getLocalizedText'
import { Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'

interface MenuItemCardProps {
  item: MenuItem
  onAddedToCart?: () => void
}

export default function MenuItemCard({ item, onAddedToCart }: MenuItemCardProps) {
  const { t, i18n } = useTranslation('customer')
  const { hasItem } = useCartStore()
  const addToCartMutation = useAddToCart()
  const priceEGP = (item.price / 100).toFixed(1)
  const isInCart = hasItem(item.id)
  const currentLanguage = (i18n.language === 'ar' ? 'ar' : 'en') as 'ar' | 'en'
  
  // Get localized name and description
  const itemName = getLocalizedText(item, 'name', currentLanguage)
  const itemDescription = getLocalizedText(item, 'description', currentLanguage)

  const handleAddToCart = () => {
    addToCartMutation.mutate(
      {
        menuItemId: item.id,
        quantity: 1,
        // Pass menu item data for optimistic update
        menuItem: {
          name: item.name,
          nameAr: item.nameAr,
          unitPrice: item.price, // Price is already in piasters
          imageUrl: item.imageUrl,
        },
      },
      {
        onSuccess: () => {
          // Call callback if provided (e.g., to open cart drawer)
          onAddedToCart?.()
        },
      }
    )
  }

  return (
    <Card className="overflow-hidden hover:shadow-lg transition-shadow duration-200">
      {/* Image Container */}
      <div className="relative aspect-video bg-slate-100 overflow-hidden">
        {item.imageUrl ? (
          <img
            src={item.imageUrl}
            alt={itemName}
            className="w-full h-full object-cover"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-slate-200 to-slate-300">
            <span className="text-slate-400 text-sm">No image</span>
          </div>
        )}
      </div>

      {/* Content */}
      <CardContent className="p-4 space-y-3">
        {/* Name - Single Language */}
        <div>
          <p
            dir={currentLanguage === 'ar' ? 'rtl' : 'ltr'}
            className={`text-sm font-semibold text-slate-900 line-clamp-2 ${currentLanguage === 'ar' ? 'text-right' : 'text-left'}`}
          >
            {itemName}
          </p>
        </div>

        {/* Description - Single Language */}
        {itemDescription && (
          <div>
            <p
              dir={currentLanguage === 'ar' ? 'rtl' : 'ltr'}
              className={`text-xs text-slate-600 line-clamp-2 ${currentLanguage === 'ar' ? 'text-right' : 'text-left'}`}
            >
              {itemDescription}
            </p>
          </div>
        )}

        {/* Category Tags */}
        {item.tags && item.tags.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {item.tags.slice(0, 2).map((tag, idx) => (
              <span
                key={idx}
                className="inline-block text-xs px-2 py-1 bg-slate-100 text-slate-700 rounded"
              >
                {tag}
              </span>
            ))}
          </div>
        )}

        {/* Allergens */}
        {item.allergens && item.allergens.length > 0 && (
          <div className="text-xs text-amber-600 pt-2 border-t border-slate-200">
            <span className="font-semibold">⚠️ Contains: </span>
            {item.allergens.join(', ')}
          </div>
        )}

        {/* Price & Add to Cart */}
        <div className="pt-3 border-t border-slate-200 space-y-3">
          <div className="flex justify-between items-center">
            <span className="text-lg font-bold text-slate-900">
              {priceEGP} EGP
            </span>
            {!item.isAvailable && (
              <span className="text-xs font-semibold text-red-600 bg-red-50 px-2 py-1 rounded">
                Unavailable
              </span>
            )}
          </div>

          {/* Add to Cart Button */}
          <Button
            onClick={handleAddToCart}
            disabled={!item.isAvailable || addToCartMutation.isPending}
            variant={isInCart ? 'outline' : 'default'}
            className="w-full"
            size="sm"
          >
            {addToCartMutation.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            {isInCart ? t('cart.inCart') : t('menu.addToCart')}
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}
